import React from 'react';
import {
  Datagrid,
  FunctionField,
  List,
  Pagination,
  ReferenceInput,
  SelectInput,
  downloadCSV,
  TopToolbar,
  ExportButton,
  DateInput,
} from 'react-admin';
import { createEmptyStateComponent } from '../../components/createEmptyStateComponent';
import { PageWrapper } from '../../components/PageWrapper';
import { IconButton, Tooltip, Box, Chip } from '@mui/material';
import { downloadFileAuthenticated } from '../../utils/downloadFile';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import GetAppIcon from '@mui/icons-material/GetApp';
import * as XLSX from 'xlsx';

const CompletedFormsEmptyState = createEmptyStateComponent('completed-forms');

const ListActions = () => (
  <TopToolbar>
    <ExportButton />
  </TopToolbar>
);

const handleDownloadPdf = async (formId: number, pdfFileId?: number) => {
  try {
    // Use the completed-forms endpoints which handle authentication properly
    const endpoint = pdfFileId
      ? `/completed-forms/${formId}/pdf`  // Stored PDF (now streams directly)
      : `/completed-forms/${formId}/make-pdf`;  // Generate PDF on-demand
    
    await downloadFileAuthenticated(endpoint, `completed-form-${formId}.pdf`);
  } catch (error) {
    console.error('PDF download failed:', error);
    // Show user-friendly error message
    alert('Failed to download PDF. Please try again or contact support.');
  }
};

export const CompletedFormsList = () => {
  // Custom exporter function that matches UI columns and adds form field values
  const exporter = React.useCallback((records: any[]) => {
    if (!Array.isArray(records) || records.length === 0) {
      downloadCSV('', 'completed-forms.csv');
      return;
    }

    // Base columns that match the UI
    const baseHeaders = [
      'Started At',
      'Completed At',
      'Completed By',
      'Form Type',
      'Vehicle/Equipment',
      'Defects',
    ];

    // Get all unique form field keys from all records
    const formFieldKeys = new Set<string>();
    records.forEach((record) => {
      if (record.formValues && typeof record.formValues === 'object') {
        Object.keys(record.formValues).forEach(key => {
          formFieldKeys.add(`formValues.${key}`);
        });
      }
    });

    // Sort form field keys alphabetically
    const sortedFormFieldKeys = Array.from(formFieldKeys).sort();

    // Combine base headers with form field headers
    const headers = [...baseHeaders, ...sortedFormFieldKeys];

    // Map records to rows
    const rows = records.map((record) => {
      // Base columns data
      const startedAt = record.startedAt ? new Date(record.startedAt).toLocaleString() : '';
      const completedAt = record.completedAt ? new Date(record.completedAt).toLocaleString() : '';
      const completedBy = record.user?.name || record.user?.email || '';
      const formType = record.form?.name || '';

      let vehicleEquipment = '-';
      if (record.equipment) {
        const year = record.equipment.manufacturedYear || '';
        const make = record.equipment.make || '';
        const model = record.equipment.model || '';
        vehicleEquipment = `${year} ${make} ${model}`.trim() || record.equipment.name || '-';
      }

      const defects = record.hasMajorDefect
        ? 'Major'
        : record.hasMinorDefect
        ? 'Minor'
        : 'None';

      const baseData = [
        startedAt,
        completedAt,
        completedBy,
        formType,
        vehicleEquipment,
        defects,
      ];

      // Form field values
      const formFieldData = sortedFormFieldKeys.map(key => {
        const fieldName = key.replace('formValues.', '');
        const value = record.formValues?.[fieldName];

        // Handle different value types
        if (value === null || value === undefined) {
          return '';
        } else if (typeof value === 'boolean') {
          return value ? 'Yes' : 'No';
        } else if (typeof value === 'object') {
          // Handle arrays and objects
          return JSON.stringify(value);
        } else {
          return String(value);
        }
      });

      return [...baseData, ...formFieldData];
    });

    // Convert to CSV using XLSX library for better handling
    const ws = XLSX.utils.aoa_to_sheet([headers, ...rows]);
    const csv = XLSX.utils.sheet_to_csv(ws);
    downloadCSV(csv, 'completed-forms.csv');
  }, []);

  const completedFormsFilters = React.useMemo(() => [
    <ReferenceInput
      key="userId"
      source="userId"
      reference="users"
      label="User"
      alwaysOn
      sort={{ field: 'name', order: 'ASC' }}
    >
      <SelectInput
        optionText="name"
        emptyText="All Users"
        sx={{
          minWidth: 250,
          '& .MuiOutlinedInput-root': {
            minWidth: 250,
            height: '56px',
          }
        }}
      />
    </ReferenceInput>,
    <ReferenceInput
      key="formId"
      source="formId"
      reference="forms"
      label="Form Type"
      alwaysOn
      sort={{ field: 'name', order: 'ASC' }}
    >
      <SelectInput
        optionText="name"
        emptyText="All Form Types"
        sx={{
          minWidth: 250,
          '& .MuiOutlinedInput-root': {
            minWidth: 250,
            height: '56px',
          }
        }}
      />
    </ReferenceInput>,
    <ReferenceInput
      key="equipmentId"
      source="equipmentId"
      reference="equipment"
      label="Equipment"
      alwaysOn
      sort={{ field: 'name', order: 'ASC' }}
    >
      <SelectInput
        optionText="name"
        emptyText="All Equipment"
        sx={{
          minWidth: 250,
          '& .MuiOutlinedInput-root': {
            minWidth: 250,
            height: '56px',
          }
        }}
      />
    </ReferenceInput>,
    <ReferenceInput
      key="locationId"
      source="locationId"
      reference="locations"
      label="Location"
      alwaysOn
      filter={{ includeInactive: true }}
      sort={{ field: 'label', order: 'ASC' }}
    >
      <SelectInput
        optionText={(record: { label?: string; active?: boolean }) =>
          record?.active === false ? `${record.label} (inactive)` : record?.label
        }
        emptyText="All Locations"
        sx={{
          minWidth: 250,
          '& .MuiOutlinedInput-root': {
            minWidth: 250,
            height: '56px',
          }
        }}
      />
    </ReferenceInput>,
    <DateInput
      key="completedDateFrom"
      source="completedDateFrom"
      label="Completed From"
      alwaysOn
      sx={{
        minWidth: 200,
        '& .MuiOutlinedInput-root': {
          minWidth: 200,
          height: '56px',
        }
      }}
    />,
    <DateInput
      key="completedDateTo"
      source="completedDateTo"
      label="Completed To"
      alwaysOn
      sx={{
        minWidth: 200,
        '& .MuiOutlinedInput-root': {
          minWidth: 200,
          height: '56px',
        }
      }}
    />,
    <SelectInput
      key="defectStatus"
      source="defectStatus"
      label="Defects"
      alwaysOn
      choices={[
        { id: 'major', name: 'Major defects' },
        { id: 'minor', name: 'Minor defects' },
        { id: 'none', name: 'No defects' },
      ]}
      emptyText="Any"
      sx={{
        minWidth: 200,
        '& .MuiOutlinedInput-root': { minWidth: 200, height: '56px' },
      }}
    />,
  ], []);
  return (
    <PageWrapper title="Completed Forms">
      <List
        resource="completed-forms/reports"
        perPage={25}
        sort={{ field: 'completedAt', order: 'DESC' }}
        empty={<CompletedFormsEmptyState />}
        pagination={<Pagination rowsPerPageOptions={[10, 25, 50, 100]} />}
        filters={completedFormsFilters}
        storeKey="completed-forms.list"
        actions={<ListActions />}
        exporter={exporter}
      >
        <Datagrid
          bulkActionButtons={false}
          rowClick={(id) => `/completed-forms/${id}/show`}
        >
        <FunctionField
          label="Started At"
          render={(record: any) => new Date(record.startedAt).toLocaleString()}
          source="startedAt"
        />
        <FunctionField
          label="Completed At"
          render={(record: any) => new Date(record.completedAt).toLocaleString()}
          source="completedAt"
        />
        <FunctionField
          label="Completed By"
          render={(record: any) => record.user?.name || record.user?.email}
        />
        <FunctionField label="Form Type" render={(record: any) => record.form?.name} />
        <FunctionField
          label="Vehicle/Equipment"
          render={(record: any) => {
            if (!record.equipment) return '-';
            return `${record.equipment.manufacturedYear || ''} ${record.equipment.make || ''} ${record.equipment.model || ''}`.trim() || record.equipment.name;
          }}
        />
        <FunctionField
          label="Defects"
          render={(record: any) => {
            if (record.hasMajorDefect) {
              return <Chip label="Major" color="error" size="small" />;
            }
            if (record.hasMinorDefect) {
              return <Chip label="Minor" color="warning" size="small" />;
            }
            return <Chip label="None" size="small" variant="outlined" />;
          }}
        />
        <FunctionField
          label="PDF"
          render={(record: any) => (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {record.pdfFileId ? (
                <Tooltip title="Download stored PDF">
                  <IconButton
                    size="small"
                    color="primary"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDownloadPdf(record.id, record.pdfFileId);
                    }}
                  >
                    <PictureAsPdfIcon />
                  </IconButton>
                </Tooltip>
              ) : (
                <Tooltip title="Generate PDF">
                  <IconButton
                    size="small"
                    color="secondary"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDownloadPdf(record.id);
                    }}
                  >
                    <GetAppIcon />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          )}
        />
        </Datagrid>
      </List>
    </PageWrapper>
  );
};


